import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { pagePropagationInstance } from 'assets/js/editor/facade/editor/handlers/header-and-footer/page-propagation.js';
import PageThumbnailFactory from 'assets/js/editor/factory/page-thumbnail';

export const setPageContent = createAction('page/set_page_content');
export const setPageThumbnail = createAction('page/set_page_thumbnail');

export const createPageThumbnail = createAsyncThunk('page/create_page_thumbnail', async (payload, { dispatch, getState }) => {
  const state = getState();

  const { id } = payload;
  const content = payload.content || state.pages[id]?.content;

  if (!content) throw new Error('Page content not found');

  const thumbnail = await PageThumbnailFactory.renderPage(id, content);

  return { id, thumbnail };
});

export const setElementsFromPage = createAsyncThunk('page/get_elements_from_page', async (payload, { dispatch }) => {
  const { id, content } = payload;

  await pagePropagationInstance.init();

  const textElements = await pagePropagationInstance.extractTextElements({ id, content });

  return { id, elements: textElements };
});

export const setPage = createAsyncThunk('page/set_page', async (payload, { dispatch, getState }) => {
  if (!payload || typeof payload !== 'object') throw new Error('Page must be an object');

  const id = Number(payload.id || payload.design_id || payload.pageId);

  const page = { id };
  const hasDimensions = ['width', 'height', 'unit'].every(key => typeof payload[key] !== 'undefined');

  if (payload.basedOn) page.basedOn = payload.basedOn;
  if (payload.width) page.width = payload.width;
  if (payload.height) page.height = payload.height;
  if (payload.thumbnail) page.thumbnail = payload.thumbnail;
  if (hasDimensions) {
    page.width = INKIVE.Helpers.convertUnitsToPixels(payload.width, payload.unit, false, 72);
    page.height = INKIVE.Helpers.convertUnitsToPixels(payload.height, payload.unit, false, 72);
  }

  if (typeof payload.content !== 'undefined') {
    dispatch(setPageContent({ id, content: payload.content }));
    await dispatch(setElementsFromPage({ id, content: payload.content }));
    payload.skipRenderThumbnail || dispatch(createPageThumbnail({ id, content: payload.content }));
  }

  return page;
});

export const fetchPage = createAsyncThunk('page/get_page', async (payload, { dispatch }) => {
  const { id, skipRenderThumbnail } = payload;
  const { content } = await INKIVE.PageFactory.getPage(id);

  await dispatch(setPage({ id, content, skipRenderThumbnail }));
});

export const fetchPages = createAsyncThunk('page/fetch_pages_content', async (payload, { dispatch }) => {
  const pages = payload.pages ?? [];
  const skipRenderThumbnail = payload.skipRenderThumbnail ?? false;

  await Promise.all(pages.map((page) => dispatch(fetchPage({ id: page, skipRenderThumbnail }))));
});

export const duplicatePage = createAction('page/duplicate_page');
export const deletePage = createAction('page/delete_page');
